import React, { useState } from 'react';
import { FaPills, FaLeaf, FaChevronDown, FaCapsules } from 'react-icons/fa';

const MedicationTable = ({ medications, herbs }) => {
  const [showMeds, setShowMeds] = useState(true);
  const [showHerbs, setShowHerbs] = useState(true);

  return (
    <div className="neo-card p-4 sm:p-6 lg:p-8 mb-6 animate-fade-in-up bg-neo-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8 border-b-2 border-neo-main pb-5 sm:pb-6">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-neo-accent text-white rounded-lg border-2 border-neo-main 
                        flex items-center justify-center shadow-neo-brutal transform -rotate-2">
            <FaCapsules className="text-2xl sm:text-3xl" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-3xl font-black text-neo-main uppercase tracking-tight break-words">
              Current Medications & Herbs
            </h2>
            <p className="text-neo-text-sub font-bold text-xs sm:text-sm mt-1">
              {medications?.length || 0} medications, {herbs?.length || 0} herbal products
            </p>
          </div>
        </div>
      </div>

      {medications && medications.length > 0 && (
        <div className="mb-8 animate-fade-in stagger-1">
          <button
            onClick={() => setShowMeds(!showMeds)}
            className={`w-full flex items-center justify-between p-3 sm:p-4 rounded-xl transition-all duration-300 border-2 border-neo-main mb-4
                      ${showMeds 
                        ? 'bg-neo-accent text-white shadow-neo-brutal' 
                        : 'bg-white hover:bg-neo-bg text-neo-text shadow-neo-sm hover:translate-x-[2px] hover:translate-y-[2px]'}`}
          >
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center border-2 border-neo-main
                            ${showMeds ? 'bg-white text-neo-main' : 'bg-neo-accent text-white'}`}>
                <FaPills />
              </div>
              <div className="text-left min-w-0">
                <h3 className="text-base sm:text-lg font-black uppercase tracking-wide">
                  Allopathic Medications
                </h3>
                <p className={`text-xs sm:text-sm font-bold ${showMeds ? 'text-white' : 'text-neo-text-sub'}`}>
                  {medications.length} active medication(s)
                </p>
              </div>
            </div>
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-transform duration-300 border-2 border-neo-main
                          ${showMeds ? 'bg-white text-neo-main rotate-180' : 'bg-neo-bg text-neo-main'}`}>
              <FaChevronDown />
            </div>
          </button>

          {showMeds && (
            <>
              <div className="sm:hidden space-y-3 animate-fade-in">
                {medications.map((med, index) => (
                  <div key={index} className="bg-white rounded-xl border-2 border-neo-main shadow-neo-brutal p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-neo-accent text-white rounded border-2 border-neo-main flex items-center justify-center shadow-neo-sm flex-shrink-0">
                        <FaPills className="text-xs" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-black text-neo-main break-words">{med.generic_name}</p>
                        {med.brand_name && (
                          <p className="text-xs font-bold text-neo-text-sub break-words">({med.brand_name})</p>
                        )}
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 bg-white border-2 border-neo-main text-neo-main text-xs font-bold rounded shadow-neo-sm flex-shrink-0">
                        {med.dose || "-"}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-black text-neo-text-sub uppercase tracking-wide">Frequency</span>
                        <span className="text-xs font-bold text-neo-text text-right break-words">{med.frequency || "-"}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-black text-neo-text-sub uppercase tracking-wide">Duration</span>
                        <span className="text-xs font-bold text-neo-text text-right break-words">
                          {med.duration?.replace("_", " ") || "-"}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-black text-neo-text-sub uppercase tracking-wide">Indication</span>
                        <span className="text-xs font-bold text-neo-text text-right break-words">
                          {med.indication || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden sm:block overflow-x-auto rounded-xl border-2 border-neo-main shadow-neo-brutal animate-fade-in">
                <table className="min-w-full divide-y-2 divide-neo-main">
                  <thead className="bg-neo-main text-white">
                    <tr>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Medication
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Dose
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Frequency
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Indication
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y-2 divide-neo-main">
                    {medications.map((med, index) => (
                      <tr
                        key={index}
                        className="hover:bg-neo-bg transition-colors duration-200"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-neo-accent text-white rounded border-2 border-neo-main 
                                        flex items-center justify-center shadow-neo-sm">
                              <FaPills className="text-xs" />
                            </div>
                            <div>
                              <p className="text-sm font-black text-neo-main">{med.generic_name}</p>
                              {med.brand_name && (
                                <p className="text-xs font-bold text-neo-text-sub">({med.brand_name})</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center px-3 py-1 bg-white border-2 border-neo-main text-neo-main 
                                       text-sm font-bold rounded shadow-neo-sm">
                            {med.dose}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-neo-text font-bold">
                          {med.frequency || '-'}
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-neo-text font-bold">
                            {med.duration?.replace('_', ' ') || '-'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-neo-text font-bold line-clamp-2">
                            {med.indication || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {herbs && herbs.length > 0 && (
        <div className="animate-fade-in stagger-2">
          <button
            onClick={() => setShowHerbs(!showHerbs)}
            className={`w-full flex items-center justify-between p-3 sm:p-4 rounded-xl transition-all duration-300 border-2 border-neo-main mb-4
                      ${showHerbs 
                        ? 'bg-neo-accent text-white shadow-neo-brutal' 
                        : 'bg-white hover:bg-neo-bg text-neo-text shadow-neo-sm hover:translate-x-[2px] hover:translate-y-[2px]'}`}
          >
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center border-2 border-neo-main
                            ${showHerbs ? 'bg-white text-neo-main' : 'bg-neo-accent text-white'}`}>
                <FaLeaf />
              </div>
              <div className="text-left min-w-0">
                <h3 className="text-base sm:text-lg font-black uppercase tracking-wide">
                  Ayurvedic / Herbal Products
                </h3>
                <p className={`text-xs sm:text-sm font-bold ${showHerbs ? 'text-white' : 'text-neo-text-sub'}`}>
                  {herbs.length} herbal product(s)
                </p>
              </div>
            </div>
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-transform duration-300 border-2 border-neo-main
                          ${showHerbs ? 'bg-white text-neo-main rotate-180' : 'bg-neo-bg text-neo-main'}`}>
              <FaChevronDown />
            </div>
          </button>

          {showHerbs && (
            <>
              <div className="sm:hidden space-y-3 animate-fade-in">
                {herbs.map((herb, index) => (
                  <div key={index} className="bg-white rounded-xl border-2 border-neo-main shadow-neo-brutal p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-neo-accent text-white rounded border-2 border-neo-main flex items-center justify-center shadow-neo-sm flex-shrink-0">
                        <FaLeaf className="text-xs" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-black text-neo-main break-words">{herb.generic_name}</p>
                        {herb.brand_name && (
                          <p className="text-xs font-bold text-neo-text-sub break-words">({herb.brand_name})</p>
                        )}
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 bg-white border-2 border-neo-main text-neo-main text-xs font-bold rounded shadow-neo-sm flex-shrink-0">
                        {herb.dose || "-"}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-black text-neo-text-sub uppercase tracking-wide">Frequency</span>
                        <span className="text-xs font-bold text-neo-text text-right break-words">{herb.frequency || "-"}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-black text-neo-text-sub uppercase tracking-wide">Duration</span>
                        <span className="text-xs font-bold text-neo-text text-right break-words">
                          {herb.duration?.replace("_", " ") || "-"}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-black text-neo-text-sub uppercase tracking-wide">Intended Effect</span>
                        <span className="text-xs font-bold text-neo-text text-right break-words">
                          {herb.intended_effect || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden sm:block overflow-x-auto rounded-xl border-2 border-neo-main shadow-neo-brutal animate-fade-in">
                <table className="min-w-full divide-y-2 divide-neo-main">
                  <thead className="bg-neo-main text-white">
                    <tr>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Herb
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Dose
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Frequency
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-black uppercase tracking-wider">
                        Intended Effect
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y-2 divide-neo-main">
                    {herbs.map((herb, index) => (
                      <tr
                        key={index}
                        className="hover:bg-neo-bg transition-colors duration-200"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-neo-accent text-white rounded border-2 border-neo-main 
                                        flex items-center justify-center shadow-neo-sm">
                              <FaLeaf className="text-xs" />
                            </div>
                            <div>
                              <p className="text-sm font-black text-neo-main">{herb.generic_name}</p>
                              {herb.brand_name && (
                                <p className="text-xs font-bold text-neo-text-sub">({herb.brand_name})</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center px-3 py-1 bg-white border-2 border-neo-main text-neo-main 
                                       text-sm font-bold rounded shadow-neo-sm">
                            {herb.dose || '-'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-neo-text font-bold">
                          {herb.frequency || '-'}
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-neo-text font-bold">
                            {herb.duration?.replace('_', ' ') || '-'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-neo-text font-bold line-clamp-2">
                            {herb.intended_effect || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {(!medications || medications.length === 0) && (!herbs || herbs.length === 0) && (
        <div className="text-center py-12 border-2 border-dashed border-neo-text-sub rounded-xl bg-neo-bg">
          <FaCapsules className="text-5xl mx-auto mb-4 text-neo-text-sub" />
          <p className="font-bold text-neo-text-sub text-lg">No medications or herbs added yet</p>
        </div>
      )}
    </div>
  );
};

export default MedicationTable;